import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Reports whether the attached input's text overflows horizontally
 * (`scrollWidth > clientWidth`), so callers can surface the full value in a
 * tooltip. Uses a callback ref + `ResizeObserver` so it re-measures across
 * remounts (e.g. expand/collapse) and layout changes, and re-measures when the
 * text `value` changes.
 */
export const useIsTextTruncated = (value: string) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const elementRef = useRef<HTMLInputElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const measure = useCallback(() => {
    const el = elementRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, []);

  const ref = useCallback(
    (el: HTMLInputElement | null) => {
      observerRef.current?.disconnect();
      elementRef.current = el;
      if (!el) {
        return;
      }
      measure();
      observerRef.current = new ResizeObserver(measure);
      observerRef.current.observe(el);
    },
    [measure]
  );

  useEffect(() => {
    measure();
  }, [value, measure]);

  return { ref, isTruncated };
};
